import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  useTheme,
  alpha,
  CircularProgress,
} from '@mui/material';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  ResponsiveContainer,
} from 'recharts';
import { useAgentReport } from '../../../contexts/AgentReportContext';
import { useLoader } from '../../../contexts/LoaderContext';

const CallsByAgentChart = ({ dateRangeState, periodType }) => {
  const theme = useTheme();
  const { fetchAgentCallPerformanceSummary, loading } = useAgentReport();
  const [chartData, setChartData] = useState([]);
  const { showLoader, hideLoader } = useLoader();

  useEffect(() => {
    fetchCallsByAgent();
  }, [dateRangeState, periodType]);

  const fetchCallsByAgent = async () => {
    showLoader();
    try {
      const startDate = dateRangeState.currentStart.format('YYYY-MM-DD');
      const endDate = dateRangeState.currentEnd.format('YYYY-MM-DD');
      const data = await fetchAgentCallPerformanceSummary('calls_by_agent', startDate, endDate);
      
      if (data && data.length > 0) {
        // Map API response to chart data format
        const mappedData = data.map(agent => ({
          agent: agent.agent || agent.name || 'Unknown',
          newCalls: agent.new_calls || 0,
          followUpCalls: agent.follow_up_calls || 0,
          notConnectedCalls: agent.not_connected_calls || 0,
          totalCalls: agent.total_calls || 0
        }));
        setChartData(mappedData);
      } else {
        // Set empty array if API returns empty
        setChartData([]);
      }
    } catch (error) {
      console.error('Error fetching calls by agent data:', error);
      // Set empty array on error
      setChartData([]);
    } finally {
      hideLoader();
    }
  };

  // Custom tooltip for the chart
  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      const total = payload.reduce((sum, entry) => sum + (entry.value || 0), 0);
      return (
        <Box
          sx={{
            backgroundColor: theme.palette.background.paper,
            border: `1px solid ${alpha(theme.palette.divider, 0.2)}`,
            borderRadius: 2,
            p: 2,
            boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
            minWidth: 180,
          }}
        >
          <Typography 
            variant="subtitle2" 
            sx={{ 
              fontWeight: 600, 
              mb: 1.5,
              color: 'text.primary',
              fontSize: '0.875rem',
            }}
          >
            {label}
          </Typography>
          {payload.map((entry, index) => (
            <Box 
              key={index} 
              sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'space-between',
                mb: 0.75,
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Box
                  sx={{
                    width: 10,
                    height: 10,
                    backgroundColor: entry.color,
                    borderRadius: '2px',
                    mr: 1,
                  }}
                />
                <Typography 
                  variant="body2" 
                  sx={{ 
                    fontSize: '0.8125rem',
                    color: 'text.secondary',
                  }}
                >
                  {entry.name}:
                </Typography>
              </Box>
              <Typography 
                variant="body2" 
                sx={{ 
                  fontSize: '0.8125rem',
                  fontWeight: 600,
                  color: 'text.primary',
                  ml: 1,
                }}
              >
                {entry.value || 0}
              </Typography>
            </Box>
          ))}
          <Box
            sx={{
              mt: 1.5,
              pt: 1.5,
              borderTop: `1px solid ${alpha(theme.palette.divider, 0.2)}`,
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
            }}
          >
            <Typography 
              variant="body2" 
              sx={{ 
                fontWeight: 600,
                color: 'text.primary',
                fontSize: '0.8125rem',
              }}
            >
              Total:
            </Typography>
            <Typography 
              variant="body2" 
              sx={{ 
                fontWeight: 700,
                color: 'text.primary',
                fontSize: '0.875rem',
              }}
            >
              {total} calls
            </Typography>
          </Box>
        </Box>
      );
    }
    return null;
  };

  if (loading) {
    return (
      <Paper
        elevation={0}
        sx={{
          p: 3,
          borderRadius: 2,
          border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
          background: 'background.paper',
        }}
      >
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
          <CircularProgress />
        </Box>
      </Paper>
    );
  }

  // Calculate max value for Y-axis domain
  const maxValue = chartData.length > 0 
    ? Math.max(...chartData.map(item => (item.totalCalls || 0)), 10)
    : 10;
  const yAxisDomain = [0, Math.ceil(maxValue * 1.1)];

  // Show empty state if no data
  if (chartData.length === 0) {
    return (
      <Paper
        elevation={0}
        sx={{
          pt: 3.5,
          px: 3.5,
          pb: 1.5,
          borderRadius: 2,
          border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
          background: 'background.paper',
        }}
      >
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2.5 }}>
          <Typography
            variant="h6"
            sx={{
              fontWeight: 700,
              color: 'text.primary',
              fontSize: '1.125rem',
            }}
          >
            Calls by Agent
          </Typography>
        </Box>
        <Box
          sx={{
            height: 360,
            width: '100%',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
          }}
        >
          <Typography
            variant="body2"
            sx={{
              color: theme.palette.text.secondary,
              fontSize: '0.875rem',
            }}
          >
            No data available for the selected period
          </Typography>
        </Box>
      </Paper>
    );
  }

  return (
    <Paper
      elevation={0}
      sx={{
        pt: 3.5,
        px: 3.5,
        pb: 1.5,
        borderRadius: 2,
        border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
        background: 'background.paper',
      }}
    >
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2.5 }}>
        <Typography
          variant="h6"
          sx={{
            fontWeight: 700,
            color: 'text.primary',
            fontSize: '1.125rem',
          }}
        >
          Calls by Agent
        </Typography>
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
            <Box
              sx={{
                width: 12,
                height: 12,
                backgroundColor: '#22c55e',
                borderRadius: '2px',
              }}
            />
            <Typography
              variant="body2"
              sx={{
                fontSize: '12px',
                color: theme.palette.text.secondary,
                fontWeight: 500,
              }}
            >
              Fresh Calls
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
            <Box
              sx={{
                width: 12,
                height: 12,
                backgroundColor: '#86efac',
                borderRadius: '2px',
              }}
            />
            <Typography
              variant="body2"
              sx={{
                fontSize: '12px',
                color: theme.palette.text.secondary,
                fontWeight: 500,
              }}
            >
              Follow-up Calls
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
            <Box
              sx={{
                width: 12,
                height: 12,
                backgroundColor: '#f87171',
                borderRadius: '2px',
              }}
            />
            <Typography
              variant="body2"
              sx={{
                fontSize: '12px',
                color: theme.palette.text.secondary,
                fontWeight: 500,
              }}
            >
              Not Connected Calls
            </Typography>
          </Box>
        </Box>
      </Box>
      <Box
        sx={{
          height: 360,
          width: '100%',
          position: 'relative',
        }}
      >
        <ResponsiveContainer width="100%" height="100%">
          <BarChart
            data={chartData}
            margin={{ top: 10, right: 20, left: 0, bottom: 80 }}
            barCategoryGap="20%"
          >
            <CartesianGrid 
              strokeDasharray="3 3" 
              stroke={alpha(theme.palette.divider, 0.4)}
              vertical={false}
            />
            <XAxis 
              dataKey="agent" 
              tick={{ 
                fontSize: 11, 
                fill: theme.palette.text.secondary,
                fontWeight: 500,
              }}
              angle={-45}
              textAnchor="end"
              height={80}
              stroke={theme.palette.text.secondary}
              interval={0}
            />
            <YAxis 
              tick={{ 
                fontSize: 11, 
                fill: theme.palette.text.secondary,
                fontWeight: 500,
              }}
              stroke={theme.palette.text.secondary}
              domain={yAxisDomain}
              allowDecimals={false}
            />
            <RechartsTooltip content={<CustomTooltip />} />
            <Bar 
              dataKey="newCalls" 
              name="Fresh Calls" 
              stackId="a" 
              fill="#22c55e"
              radius={[0, 0, 0, 0]}
            />
            <Bar 
              dataKey="followUpCalls" 
              name="Follow-up Calls" 
              stackId="a" 
              fill="#86efac"
              radius={[0, 0, 0, 0]}
            />
            <Bar 
              dataKey="notConnectedCalls" 
              name="Not Connected Calls" 
              stackId="a" 
              fill="#f87171"
              radius={[0, 0, 0, 0]}
            />
          </BarChart>
        </ResponsiveContainer>
      </Box>
    </Paper>
  );
};

export default CallsByAgentChart;

